import { FC, useCallback, KeyboardEvent, MouseEvent, useState, useRef } from 'react';
import classNames from 'classnames';

import '@hh.ru/magritte-common-focus-visible';
import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { Action } from '@hh.ru/magritte-ui-action';
import { BottomSheet } from '@hh.ru/magritte-ui-bottom-sheet';
import { FormLabelProps } from '@hh.ru/magritte-ui-form-label/types';
import { QuestionCircleOutlinedSize16, CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import { Tooltip } from '@hh.ru/magritte-ui-tooltip';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './form-label.less';

export const FormLabel: FC<FormLabelProps> = (props) => {
    const disabled = useDisabled();
    const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const activatorRef = useRef<HTMLSpanElement | null>(null);

    const {
        children,
        info,
        postfix,
        styleText = 'secondary',
        stylePostfix = 'tertiary',
        styleIcon = 'secondary',
        'data-qa': dataQa = 'form-label',
        ...restProps
    } = props;

    const onClick = useCallback(
        (event: MouseEvent) => {
            if (info) {
                event.preventDefault();
            }

            if (!disabled) {
                setShowBottomSheet(true);
            }
        },
        [disabled, info]
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLLabelElement>) => {
            if (disabled || !onClick) {
                return;
            }

            if (keyboardMatches(event.nativeEvent, [keyboardKeys.Enter, keyboardKeys.Space])) {
                onClick(event as unknown as MouseEvent<HTMLLabelElement>);
            }
        },
        [onClick, disabled]
    );

    const propsForInfo = info
        ? {
              tabIndex: disabled ? -1 : 0,
              role: 'button',
              onKeyDown,
              onClick,
              onMouseEnter: () => setShowTooltip(true),
              onMouseLeave: () => setShowTooltip(false),
              onFocus: () => setShowTooltip(true),
              onBlur: () => setShowTooltip(false),
          }
        : {};

    return (
        <>
            <label
                className={classNames(styles.formLabel, {
                    [styles.formLabelDisabled]: disabled,
                    [styles[`style-icon__${styleIcon}`]]: styleIcon,
                    [styles[`style-postfix__${stylePostfix}`]]: stylePostfix,
                    [styles[`style-text__${styleText}`]]: styleText,
                })}
                data-qa={dataQa}
                {...propsForInfo}
                {...restProps}
            >
                <span className={styles.content}>{children}</span>
                {postfix && (
                    <span className={styles.postfix} data-qa="form-label-postfix">
                        {postfix}
                    </span>
                )}
                {info && (
                    <span data-qa="form-label-info-icon" className={styles.icon} ref={activatorRef}>
                        <QuestionCircleOutlinedSize16 />
                    </span>
                )}
            </label>
            {info && (
                <>
                    <Tooltip
                        maxWidth={300}
                        visible={showTooltip}
                        activatorRef={activatorRef}
                        placement="top-center"
                        data-qa="form-label-tooltip"
                    >
                        {info}
                    </Tooltip>
                    <BottomSheet
                        visible={showBottomSheet}
                        onClose={() => {
                            setShowBottomSheet(false);
                        }}
                        header={
                            <NavigationBar
                                title={children}
                                right={<Action icon={CrossOutlinedSize24} onClick={() => setShowBottomSheet(false)} />}
                                showDivider="always"
                            />
                        }
                        data-qa="form-label-bottom-sheet"
                    >
                        <Text typography="label-2-regular">{info}</Text>
                        <VSpacing default={16} />
                    </BottomSheet>
                </>
            )}
        </>
    );
};
